/**
 * Who is using the app right now — account holder or guest.
 *
 * Two independent session sources sit behind this. Better Auth's `useSession`
 * knows about accounts and nothing else; `GET /api/guest/session` knows about
 * people playing one circle by link and PIN (PLAN.md Phase 10.29). Every screen
 * that just needs "is someone here, and what are they called" asks this instead
 * of picking one, so adding the second kind of session did not mean auditing
 * every component for which of the two it happened to consult.
 *
 * An account wins when both are somehow present — the same precedence the API
 * applies in `auth/context.ts`, and the state a guest is briefly in while
 * upgrading, where being treated as the account they now have is the point.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../api.js';
import { useSession } from '../auth-client.js';

export interface Actor {
  userId: string;
  name: string;
  imageUrl: string | null;
  /** Non-null for a guest: the one circle they can reach. Null for an account holder. */
  guestGroupId: string | null;
}

export interface ActorState {
  actor: Actor | null;
  isPending: boolean;
  /** True while someone is signed in as a guest — the app's reduced mode. */
  isGuest: boolean;
}

/** Shared query key, so signing in or out anywhere can invalidate this one cache entry. */
export const GUEST_SESSION_KEY = ['guest-session'] as const;

export function useActor(): ActorState {
  const { data: session, isPending: sessionPending } = useSession();

  const { data: guestData, isPending: guestPending } = useQuery({
    queryKey: GUEST_SESSION_KEY,
    queryFn: api.getGuestSession,
    // A failed guest-session probe means "not a guest", never a broken app:
    // this fires on every page load, including for people who will only ever
    // sign in with Google.
    retry: false,
  });

  if (session?.user) {
    const user = session.user as { id: string; name: string; image?: string | null };
    return {
      actor: {
        userId: user.id,
        name: user.name,
        imageUrl: user.image ?? null,
        guestGroupId: null,
      },
      isPending: false,
      isGuest: false,
    };
  }

  const guest = guestData?.guest;
  if (guest) {
    return {
      actor: {
        userId: guest.userId,
        name: guest.name,
        imageUrl: guest.image,
        guestGroupId: guest.groupId,
      },
      isPending: false,
      isGuest: true,
    };
  }

  // Only "nobody" once both sources have actually answered — reporting it
  // early would bounce a signed-in guest to the sign-in screen on every hard
  // refresh.
  return { actor: null, isPending: sessionPending || guestPending, isGuest: false };
}
